"use client";

import Link from "next/link";
import Image from "next/image";
import { useState, useEffect } from "react";

export default function WhyEMSPulse() {
  // State to track if component is mounted
  const [isMounted, setIsMounted] = useState(false);

  // Set mounted state after component loads
  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative py-20 sm:py-24 md:py-28 px-4 bg-gradient-to-br from-[#1a2942] via-[#234563] to-[#1e5e52] text-white overflow-hidden min-h-[460px]"
      >
        {/* Abstract shapes */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 right-1/4 w-60 sm:w-80 md:w-96 h-60 sm:h-80 md:h-96 rounded-full bg-[#38b5a0] blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 left-1/5 w-60 sm:w-70 md:w-80 h-60 sm:h-70 md:h-80 rounded-full bg-[#f8c156] blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
          <div className="absolute top-40 left-1/3 w-40 sm:w-50 md:w-60 h-40 sm:h-50 md:h-60 rounded-full bg-[#38b5a0]/40 blur-3xl animate-pulse" style={{animationDelay: '1.5s'}}></div>
        </div>
        
        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <span 
            className="inline-block bg-[#38b5a0]/20 text-white text-lg mb-4 py-1 px-4 rounded-full border border-[#38b5a0]/30 animate-[fadeSlideUp_0.7s_0.2s_forwards] will-change-transform will-change-opacity" 
            style={{opacity: 0}}
          >
            Why Choose Us
          </span>
          <h1 
            className="text-3xl md:text-5xl font-bold mb-6 leading-tight animate-[fadeSlideUp_0.7s_0.4s_forwards] will-change-transform will-change-opacity" 
            style={{opacity: 0}}
          >
            Partner with the UK's Leading EMS Technology Provider
          </h1>
          <p 
            className="text-xl opacity-90 max-w-3xl mx-auto animate-[fadeSlideUp_0.7s_0.6s_forwards] will-change-transform will-change-opacity" 
            style={{opacity: 0}}
          >
            Experience unparalleled support, expertise, and success with EMS Pulse as your official I-motion partner.
          </p>
        </div>
        
        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden">
          <svg
            data-name="Layer 1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
            className="w-full h-[60px]"
          >
            <path
              d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
              className="fill-white"
            ></path>
          </svg>
        </div>
      </section>

      {/* Define the animations so they're available in this component */}
      <style jsx global>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes fadeSlideUp {
          from { 
            opacity: 0;
            transform: translateY(20px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      {/* Story Section */}
      <section className="py-12 md:py-20 px-4 bg-gradient-to-br from-[#f4fbfa] to-white">
        <div className="max-w-5xl mx-auto">
          <span className="inline-block bg-[#38b5a0]/10 text-[#38b5a0] text-xs md:text-sm font-semibold mb-3 py-1 px-3 rounded-full">
            OUR STORY
          </span>
          <h2 className="text-[#1a2942] text-2xl md:text-4xl font-bold mb-6 md:mb-8">The EMS Pulse Journey</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-center">
            <div className="md:col-span-7">
              <p className="text-[#455a73] mb-4 md:mb-6 text-base md:text-lg">
                EMS Pulse was founded with a clear mission: to bring the most advanced EMS technology to the UK market. As fitness and rehabilitation specialists, we recognized the transformative potential of whole-body EMS training and sought to partner with the industry leader.
              </p>
              <p className="text-[#455a73] mb-4 md:mb-6 text-base md:text-lg">
                Our partnership with I-motion represents our commitment to quality, innovation, and results. We don't just distribute equipment – we provide a complete business solution tailored to the UK market.
              </p>
              <ul className="space-y-3 mt-6 md:mt-8">
                <li className="flex items-start">
                  <span className="flex-shrink-0 p-1 bg-[#38b5a0]/10 rounded-full mr-3 mt-1">
                    <svg className="w-4 h-4 md:w-5 md:h-5 text-[#38b5a0]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </span>
                  <span className="text-[#455a73] text-sm md:text-base">Exclusive UK distributor for I-motion EMS systems</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 p-1 bg-[#38b5a0]/10 rounded-full mr-3 mt-1">
                    <svg className="w-4 h-4 md:w-5 md:h-5 text-[#38b5a0]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </span>
                  <span className="text-[#455a73] text-sm md:text-base">Complete business support from setup to ongoing operations</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 p-1 bg-[#38b5a0]/10 rounded-full mr-3 mt-1">
                    <svg className="w-4 h-4 md:w-5 md:h-5 text-[#38b5a0]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </span>
                  <span className="text-[#455a73] text-sm md:text-base">Certified training and ongoing education for EMS professionals</span>
                </li>
              </ul>
            </div>
            <div className="md:col-span-5 relative mt-6 md:mt-0">
              <div className="rounded-lg overflow-hidden shadow-xl relative z-10 bg-gradient-to-br from-white to-[#f4fbfa]">
                <div className="p-2">
                  <Image 
                    src="/images/ems-business-manager.jpg" 
                    alt="EMS Pulse business manager" 
                    className="w-full h-auto block rounded-md"
                    width={600}
                    height={450}
                    priority={true}
                    sizes="(max-width: 768px) 100vw, 500px"
                    placeholder="blur"
                    blurDataURL="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+P+/HgAFeAKNn0EgSgAAAABJRU5ErkJggg=="
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#38b5a0]/10 to-transparent pointer-events-none"></div>
              </div>
              <div className="absolute -z-10 w-full h-full -top-4 -right-4 bg-[#38b5a0]/10 rounded-lg"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 sm:py-20 px-4 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <span className="inline-block bg-[#38b5a0]/10 text-[#38b5a0] font-medium px-3 sm:px-4 py-1 rounded-full text-xs sm:text-sm mb-3 sm:mb-4">
              OUR ADVANTAGES
            </span>
            <h2 className="text-xl sm:text-2xl md:text-4xl font-bold mb-2 sm:mb-3 text-[#1a2942]">
              Why Partner with EMS Pulse
            </h2>
            <p className="text-sm sm:text-base text-[#455a73] max-w-2xl mx-auto">
              We deliver more than just premium technology—we provide a complete partnership solution
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
            <div className="bg-white rounded-xl border border-slate-200 shadow-md hover:shadow-lg p-5 sm:p-8 transition-all hover:translate-y-[-3px]">
              <div className="bg-[#287e6f] p-2.5 sm:p-3 rounded-xl text-white inline-block mb-3 sm:mb-4 shadow-md shadow-[#287e6f]/10">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 sm:w-6 sm:h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                </svg>
              </div>
              <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3 text-[#1a2942]">Official UK Distributor</h3>
              <p className="text-sm sm:text-base text-[#455a73]">
                We're the exclusive official I-motion partner for the UK, ensuring you receive genuine equipment with full manufacturer support.
              </p>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200 shadow-md hover:shadow-lg p-5 sm:p-8 transition-all hover:translate-y-[-3px]">
              <div className="bg-[#287e6f] p-2.5 sm:p-3 rounded-xl text-white inline-block mb-3 sm:mb-4 shadow-md shadow-[#287e6f]/10">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 sm:w-6 sm:h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5" />
                </svg>
              </div>
              <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3 text-[#1a2942]">Expert Training Program</h3>
              <p className="text-sm sm:text-base text-[#455a73]">
                Our comprehensive EMS Pulse certification program ensures your team masters the technology and delivery methods.
              </p>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200 shadow-md hover:shadow-lg p-5 sm:p-8 transition-all hover:translate-y-[-3px]">
              <div className="bg-[#287e6f] p-2.5 sm:p-3 rounded-xl text-white inline-block mb-3 sm:mb-4 shadow-md shadow-[#287e6f]/10">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 sm:w-6 sm:h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
                </svg>
              </div>
              <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3 text-[#1a2942]">Dedicated UK Support</h3>
              <p className="text-sm sm:text-base text-[#455a73]">
                Receive responsive local support and maintenance from professionals who understand the UK fitness market.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-12 md:py-20 px-4 bg-gradient-to-br from-white to-[#e8f5f2]">
        <div className="max-w-5xl mx-auto">
          <span className="inline-block bg-[#38b5a0]/10 text-[#38b5a0] text-xs md:text-sm font-semibold mb-3 py-1 px-3 rounded-full">
            OUR VALUES
          </span>
          <h2 className="text-[#1a2942] text-2xl md:text-4xl font-bold mb-8 md:mb-12">The Core Values That Drive Us</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
            {/* Value Card 1 */}
            <div className="bg-white p-4 md:p-6 rounded-xl shadow-sm border border-[#e8f5f2] hover:shadow-md transition-shadow">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#38b5a0]/10 flex items-center justify-center mb-4 md:mb-5">
                <svg className="w-6 h-6 md:w-7 md:h-7 text-[#38b5a0]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                </svg>
              </div>
              <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3 text-[#1a2942]">Safety First</h3>
              <p className="text-sm md:text-base text-[#455a73]">
                We prioritize safety in all aspects of our business, from equipment certification to training protocols.
              </p>
            </div>
            
            {/* Value Card 2 */}
            <div className="bg-white p-4 md:p-6 rounded-xl shadow-sm border border-[#e8f5f2] hover:shadow-md transition-shadow">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#f8c156]/10 flex items-center justify-center mb-4 md:mb-5">
                <svg className="w-6 h-6 md:w-7 md:h-7 text-[#f8c156]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                </svg>
              </div>
              <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3 text-[#1a2942]">Innovation</h3>
              <p className="text-sm md:text-base text-[#455a73]">
                We continuously seek cutting-edge solutions in EMS technology and business support systems.
              </p>
            </div>
            
            {/* Value Card 3 */}
            <div className="bg-white p-4 md:p-6 rounded-xl shadow-sm border border-[#e8f5f2] hover:shadow-md transition-shadow">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#2389db]/10 flex items-center justify-center mb-4 md:mb-5">
                <svg className="w-6 h-6 md:w-7 md:h-7 text-[#2389db]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"></path>
                </svg>
              </div>
              <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3 text-[#1a2942]">Partnership</h3>
              <p className="text-sm md:text-base text-[#455a73]">
                We build lasting relationships with our clients, providing ongoing support for their success.
              </p>
            </div>
            
            {/* Value Card 4 */}
            <div className="bg-white p-4 md:p-6 rounded-xl shadow-sm border border-[#e8f5f2] hover:shadow-md transition-shadow">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#e05c88]/10 flex items-center justify-center mb-4 md:mb-5">
                <svg className="w-6 h-6 md:w-7 md:h-7 text-[#e05c88]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"></path>
                </svg>
              </div>
              <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3 text-[#1a2942]">Quality</h3>
              <p className="text-sm md:text-base text-[#455a73]">
                We deliver only the highest quality products with industry-leading certifications and reliability.
              </p>
            </div>
            
            {/* Value Card 5 */}
            <div className="bg-white p-4 md:p-6 rounded-xl shadow-sm border border-[#e8f5f2] hover:shadow-md transition-shadow">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#38b5a0]/10 flex items-center justify-center mb-4 md:mb-5">
                <svg className="w-6 h-6 md:w-7 md:h-7 text-[#38b5a0]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"></path>
                </svg>
              </div>
              <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3 text-[#1a2942]">Adaptability</h3>
              <p className="text-sm md:text-base text-[#455a73]">
                We tailor our solutions to meet the specific needs of each client's business model and goals.
              </p>
            </div>
            
            {/* Value Card 6 */}
            <div className="bg-white p-4 md:p-6 rounded-xl shadow-sm border border-[#e8f5f2] hover:shadow-md transition-shadow">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#f8c156]/10 flex items-center justify-center mb-4 md:mb-5">
                <svg className="w-6 h-6 md:w-7 md:h-7 text-[#f8c156]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 016 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                </svg>
              </div>
              <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3 text-[#1a2942]">Expertise</h3>
              <p className="text-sm md:text-base text-[#455a73]">
                Our team combines deep knowledge of EMS technology with business development experience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials & Benefits */}
      <section className="py-12 md:py-16 px-4 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8 md:mb-10">
            <span className="inline-block bg-[#38b5a0]/10 text-[#38b5a0] font-medium px-3 sm:px-4 py-1 rounded-full text-xs sm:text-sm mb-2 sm:mb-3">
              PARTNER EXPERIENCE
            </span>
            <h2 className="text-xl sm:text-2xl md:text-4xl font-bold mb-2 sm:mb-3 text-[#1a2942]">
              The EMS Pulse Difference
            </h2>
            <p className="text-sm sm:text-base text-[#455a73] max-w-2xl mx-auto">
              What our partners say about working with us
            </p>
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 shadow-md hover:shadow-lg p-4 sm:p-5 md:p-8 mb-6 sm:mb-8 md:mb-12 transition-all">
            <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4 md:mb-6">
              <div className="bg-[#38b5a0] p-2 sm:p-2.5 md:p-3 rounded-full text-white shadow-md shadow-[#38b5a0]/10">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
                </svg>
              </div>
              <div>
                <p className="text-sm sm:text-base md:text-lg font-bold text-[#1a2942]">Partner Testimonial</p>
                <p className="text-xs sm:text-sm text-[#455a73]">Fitness Business Owner, London</p>
              </div>
            </div>
            
            <blockquote className="text-[#455a73] italic text-sm sm:text-base md:text-lg mb-3 sm:mb-4">
              "Working with EMS Pulse has transformed our business. Beyond the excellent I-motion equipment, 
              their ongoing support and training have been invaluable. Their team is always responsive and 
              genuinely interested in our success. The ROI has exceeded our expectations within just 8 months."
            </blockquote>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
            <div className="bg-white rounded-xl border border-slate-200 shadow-md hover:shadow-lg p-4 sm:p-5 md:p-8 transition-all">
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-2 sm:mb-3 md:mb-4 text-[#1a2942]">ROI Focused Partnership</h3>
              <p className="text-sm sm:text-base text-[#455a73] mb-3 sm:mb-4 md:mb-5">
                We don't just supply equipment—we help you build a profitable business model with high-margin EMS training services. Our business case provides clear projections and timeline to profitability.
              </p>
              <div className="bg-[#38b5a0]/5 p-3 sm:p-4 rounded-lg border border-[#38b5a0]/10">
                <p className="text-xs sm:text-sm flex items-start gap-2">
                  <span className="text-[#38b5a0] mt-0.5 flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                      <path fillRule="evenodd" d="M11 2a1 1 0 10-2 0v6.5a.5.5 0 01-1 0V3a1 1 0 10-2 0v5.5a.5.5 0 01-1 0V5a1 1 0 10-2 0v7a7 7 0 1014 0V8a1 1 0 10-2 0v3.5a.5.5 0 01-1 0V2z" clipRule="evenodd" />
                    </svg>
                  </span>
                  Typical facilities see breakeven on equipment within 6-9 months when following our implementation strategy
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200 shadow-md hover:shadow-lg p-4 sm:p-5 md:p-8 transition-all">
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-2 sm:mb-3 md:mb-4 text-[#1a2942]">Ongoing Business Support</h3>
              <p className="text-sm sm:text-base text-[#455a73] mb-3 sm:mb-4 md:mb-5">
                From initial integration to ongoing optimization, we remain involved as a support partner. Our success depends directly on your growth, so we're invested in exceeding your revenue targets.
              </p>
              <div className="bg-[#38b5a0]/5 p-3 sm:p-4 rounded-lg border border-[#38b5a0]/10">
                <p className="text-xs sm:text-sm flex items-start gap-2">
                  <span className="text-[#38b5a0] mt-0.5 flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                      <path d="M10 9a3 3 0 100-6 3 3 0 000 6zM6 8a2 2 0 11-4 0 2 2 0 014 0zM1.49 15.326a.78.78 0 01-.358-.442 3 3 0 014.308-3.516 6.484 6.484 0 00-1.905 3.959c-.023.222-.014.442.025.654a4.97 4.97 0 01-2.07-.655zM16.44 15.98a4.97 4.97 0 002.07-.654.78.78 0 00.357-.442 3 3 0 00-4.308-3.517 6.484 6.484 0 011.907 3.96 2.32 2.32 0 01-.026.654zM18 8a2 2 0 11-4 0 2 2 0 014 0zM5.304 16.19a.844.844 0 01-.277-.71 5 5 0 019.947 0 .843.843 0 01-.277.71A6.975 6.975 0 0110 18a6.974 6.974 0 01-4.696-1.81z" />
                    </svg>
                  </span>
                  Partners receive quarterly business reviews and strategy sessions to maximize revenue potential
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comprehensive Support */}
      <section className="py-16 sm:py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-xl border border-slate-200 shadow-md hover:shadow-lg p-6 sm:p-8 md:p-10">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6 sm:mb-8">
              <div className="bg-[#38b5a0]/10 p-3 sm:p-4 rounded-xl text-[#38b5a0] self-start sm:self-auto shadow-md">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 sm:w-8 sm:h-8">
                  <path fillRule="evenodd" d="M19.5 2.25a.75.75 0 00-1.5 0v16.5h-2.25a.75.75 0 000 1.5h3a.75.75 0 00.75-.75V2.25zM11.25 8.25a3.75 3.75 0 117.5 0 3.75 3.75 0 01-7.5 0zm0 0a3.75 3.75 0 117.5 0 3.75 3.75 0 01-7.5 0zm-6 0a2.25 2.25 0 104.5 0 2.25 2.25 0 00-4.5 0z" clipRule="evenodd" />
                </svg>
              </div>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1a2942]">
                End-to-End Support for Your Business
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-5 sm:gap-6">
              <div className="bg-slate-50 rounded-lg p-4 sm:p-5 border border-slate-100 shadow-md hover:shadow-lg transition-all">
                <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3 text-[#1a2942]">Initial Setup & Installation</h3>
                <p className="text-sm sm:text-base text-[#455a73]">
                  Professional installation of your I-motion system with comprehensive staff orientation
                </p>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-4 sm:p-5 border border-slate-100 shadow-md hover:shadow-lg transition-all">
                <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3 text-[#1a2942]">Full Certification Program</h3>
                <p className="text-sm sm:text-base text-[#455a73]">
                  Complete training for your team, including EMS science, client management, and system operation
                </p>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-4 sm:p-5 border border-slate-100 shadow-md hover:shadow-lg transition-all">
                <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3 text-[#1a2942]">Marketing Support</h3>
                <p className="text-sm sm:text-base text-[#455a73]">
                  Client acquisition strategies, promotional materials, and business growth consultation
                </p>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-4 sm:p-5 border border-slate-100 shadow-md hover:shadow-lg transition-all">
                <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3 text-[#1a2942]">Ongoing Technical Support</h3>
                <p className="text-sm sm:text-base text-[#455a73]">
                  Dedicated UK-based support team with fast response times and proactive maintenance
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-[#f8fafa] via-white to-[#f4f8fc]">
        <div className="max-w-5xl mx-auto">
          <span className="inline-block bg-[#f8c156]/10 text-[#f8c156] text-sm font-semibold mb-3 py-1 px-3 rounded-full">
            WHY CHOOSE US
          </span>
          <h2 className="text-[#1a2942] text-3xl md:text-4xl font-bold mb-12">The EMS Pulse Advantage</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            {/* Left Column */}
            <div className="space-y-8">
              {/* Benefit 1 */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-[#e8f5f2] hover:shadow-md transition-shadow">
                <div className="flex items-start">
                  <div className="mr-5">
                    <div className="w-12 h-12 rounded-lg bg-[#38b5a0]/10 flex items-center justify-center">
                      <svg className="w-6 h-6 text-[#38b5a0]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                      </svg>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-[#1a2942]">Cutting-Edge Technology</h3>
                    <p className="text-[#455a73]">
                      Exclusive access to I-motion's advanced wireless EMS systems, continually refined through German engineering excellence.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Benefit 2 */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-[#e8f5f2] hover:shadow-md transition-shadow">
                <div className="flex items-start">
                  <div className="mr-5">
                    <div className="w-12 h-12 rounded-lg bg-[#2389db]/10 flex items-center justify-center">
                      <svg className="w-6 h-6 text-[#2389db]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                      </svg>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-[#1a2942]">Complete Business Solution</h3>
                    <p className="text-[#455a73]">
                      Beyond equipment, we provide marketing materials, business templates, and ongoing operational support.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Benefit 3 */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-[#e8f5f2] hover:shadow-md transition-shadow">
                <div className="flex items-start">
                  <div className="mr-5">
                    <div className="w-12 h-12 rounded-lg bg-[#f8c156]/10 flex items-center justify-center">
                      <svg className="w-6 h-6 text-[#f8c156]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                      </svg>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-[#1a2942]">Superior Safety Standards</h3>
                    <p className="text-[#455a73]">
                      All equipment meets or exceeds UK and EU safety regulations with comprehensive certification documentation.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right Column */}
            <div className="space-y-8">
              {/* Benefit 4 */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-[#e8f5f2] hover:shadow-md transition-shadow">
                <div className="flex items-start">
                  <div className="mr-5">
                    <div className="w-12 h-12 rounded-lg bg-[#e05c88]/10 flex items-center justify-center">
                      <svg className="w-6 h-6 text-[#e05c88]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"></path>
                      </svg>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-[#1a2942]">Expert Training Program</h3>
                    <p className="text-[#455a73]">
                      Comprehensive certification courses for your trainers with ongoing education and advanced technique workshops.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Benefit 5 */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-[#e8f5f2] hover:shadow-md transition-shadow">
                <div className="flex items-start">
                  <div className="mr-5">
                    <div className="w-12 h-12 rounded-lg bg-[#38b5a0]/10 flex items-center justify-center">
                      <svg className="w-6 h-6 text-[#38b5a0]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                      </svg>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-[#1a2942]">Proven ROI Model</h3>
                    <p className="text-[#455a73]">
                      Clear financial projections and business models demonstrating the exceptional return on investment of EMS training.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Benefit 6 */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-[#e8f5f2] hover:shadow-md transition-shadow">
                <div className="flex items-start">
                  <div className="mr-5">
                    <div className="w-12 h-12 rounded-lg bg-[#2389db]/10 flex items-center justify-center">
                      <svg className="w-6 h-6 text-[#2389db]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                      </svg>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-[#1a2942]">Dedicated Support Team</h3>
                    <p className="text-[#455a73]">
                      Quick response technical support and regular check-ins from our UK-based team of EMS specialists.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-12 text-center">
            <a href="/roi" className="inline-flex items-center bg-[#287e6f] hover:bg-[#1e665a] text-white font-semibold py-3 px-6 rounded-lg transition-colors">
              See Our ROI Calculator
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
              </svg>
            </a>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-gradient-to-br from-slate-900 via-slate-800 to-[#1e5e52] text-white relative overflow-hidden">
        {/* Abstract shapes */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 right-1/4 w-64 md:w-96 h-64 md:h-96 rounded-full bg-[#32a895] blur-3xl"></div>
          <div className="absolute bottom-10 left-1/5 w-56 md:w-80 h-56 md:h-80 rounded-full bg-[#32a895] blur-3xl"></div>
        </div>
        
        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <h2 className="text-xl sm:text-2xl md:text-4xl font-bold mb-4 sm:mb-6 leading-tight">
            Ready to Elevate Your Fitness Business with I-motion Technology?
          </h2>
          <p className="text-base sm:text-lg opacity-90 max-w-3xl mx-auto mb-6 sm:mb-8 md:mb-10">
            Join the growing network of UK fitness businesses experiencing the EMS Pulse advantage with I-motion technology
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="/contact"
              className="bg-[#287e6f] text-white font-medium px-5 sm:px-6 md:px-8 py-3 sm:py-4 rounded-lg hover:bg-[#1e665a] transition-all shadow-md shadow-[#287e6f]/10 min-w-[160px] text-center text-base"
            >
              Request a Demo
            </Link>
            <Link
              href="/systems"
              className="bg-white/10 text-white border border-white/20 font-medium px-5 sm:px-6 md:px-8 py-3 sm:py-4 rounded-lg hover:bg-white/20 transition-all shadow-md shadow-black/5 backdrop-blur-sm min-w-[160px] text-center text-base"
            >
              Explore Systems
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
} 